import { ScrollText } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { usePolicies, type AttachedPolicy } from '@/api/s3';
import { Mono } from '../s3-parts';
import { AttachPicker } from './AttachPicker';

/**
 * Attached managed-policies editor shared by the user / group / role detail
 * pages. Lists current attachments (detach per row) and offers a picker of the
 * service's policies that are not yet attached. The parent owns the mutations
 * (keyed by its entity name) and passes bound `onAttach`/`onDetach` callbacks.
 */
export function PolicyAttachments({
  serviceId,
  attached,
  onAttach,
  onDetach,
  pending,
}: {
  serviceId: string;
  attached: AttachedPolicy[];
  onAttach: (policyArn: string) => void;
  onDetach: (policyArn: string) => void;
  pending?: boolean;
}) {
  const { data: policies = [] } = usePolicies(serviceId);
  const attachedArns = new Set(attached.map((p) => p.policy_arn));
  const options = policies
    .filter((p) => !attachedArns.has(p.arn))
    .map((p) => ({ value: p.arn, label: p.policy_name }));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <AttachPicker
        options={options}
        onAdd={onAttach}
        pending={pending}
        placeholder="Select a policy…"
        addLabel="Attach"
        emptyHint="No more policies available to attach."
      />

      {attached.length === 0 ? (
        <EmptyState
          icon={<ScrollText size={20} />}
          title="No policies attached yet"
          description="Attach a managed policy to grant scoped access."
        />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--vn-space-2)' }}>
          {attached.map((p) => (
            <li
              key={p.policy_arn}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--vn-space-3)',
                border: '1px solid var(--vn-border-subtle)',
                borderRadius: 'var(--vn-radius-md)',
                padding: 'var(--vn-space-2) var(--vn-space-3)',
              }}
            >
              <Mono>{p.policy_name}</Mono>
              <Button
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={() => onDetach(p.policy_arn)}
              >
                Detach
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
